import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { orders as ordersApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Package, Eye, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const OrdersPage = () => {
  usePageTitle('My Orders');
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);
  const { isAdmin } = useAuth();
  const { fetchCart } = useCart();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await ordersApi.getAll();
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (orderId) => {
    setReordering(orderId);
    try {
      const response = await ordersApi.reorder(orderId);
      const data = response.data;
      
      // Refresh the cart context
      await fetchCart();
      
      // Show success message
      if (data.items_failed && data.items_failed.length > 0) {
        toast.warning(`${data.items_added.length} items added to cart. ${data.items_failed.length} items couldn't be added.`, {
          description: data.items_failed.map(f => `${f.product_name}: ${f.reason}`).join(', ')
        });
      } else {
        toast.success('Order items have been added to your cart!');
      }
      
      // Navigate to cart
      navigate('/cart');
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error(error.response?.data?.detail || 'Failed to reorder. Please try again.');
    } finally {
      setReordering(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-purple-100 text-purple-800 border-purple-200',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="container-wide py-8">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-[#F1F5F9]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      <h1 className="text-3xl font-bold text-[#0F172A] mb-8">
        {isAdmin ? 'All Orders' : 'My Orders'}
      </h1>

      {orders.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0F172A] mb-2">No Orders Yet</h2>
          <p className="text-[#64748B] mb-8">Start shopping to place your first order</p>
          <Link to="/products">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
              Browse Products
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              className="border border-[#E2E8F0] p-6 hover:border-[#002FA7] transition-colors"
              data-testid={`order-${order.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <p className="font-mono text-sm text-[#64748B]">{order.order_number}</p>
                  <p className="text-lg font-semibold text-[#0F172A]">
                    {isAdmin && order.company_name}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={`${getStatusColor(order.status)} border`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </Badge>
                  <span className="text-xl font-bold text-[#002FA7]">
                    ${order.total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="text-sm text-[#64748B]">
                  <p>
                    {order.items.length} item(s) •{' '}
                    {order.items.reduce((acc, item) => acc + item.total_quantity, 0)} units
                  </p>
                  <p>
                    {new Date(order.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!isAdmin && order.status !== 'cancelled' && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleReorder(order.id)}
                      disabled={reordering === order.id}
                      data-testid={`reorder-${order.id}`}
                    >
                      {reordering === order.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Reorder
                    </Button>
                  )}
                  <Link to={`/orders/${order.id}`}>
                    <Button variant="outline" size="sm" data-testid={`view-order-${order.id}`}>
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
